/**
 * Product draft completeness - used by KAI to guide merchants after AI image apply.
 */

const FIELD_DEFS = [
  {
    key: "name",
    label: "Product name",
    section: "general",
    required: true,
    getValue: (d) => d?.productInformation?.name?.trim(),
    prompt: "What should we call this product?",
  },
  {
    key: "type",
    label: "Product type",
    section: "general",
    required: true,
    getValue: (d) => d?.productType?.name?.trim(),
    prompt: "Is this a Standard or Variant product?",
  },
  {
    key: "condition",
    label: "Condition",
    section: "general",
    required: true,
    getValue: (d) => d?.productCondition?.name?.trim(),
    prompt: "What condition is it? New, Used, or Refurbished?",
  },
  {
    key: "summary",
    label: "Summary",
    section: "general",
    required: true,
    getValue: (d) => d?.productInformation?.summary?.trim(),
    prompt: "Give me a one-line summary for shoppers.",
  },
  {
    key: "brand",
    label: "Brand",
    section: "general",
    required: true,
    getValue: (d) => d?.brand?.name?.trim(),
    prompt: "Which brand does this belong to?",
  },
  {
    key: "categories",
    label: "Categories",
    section: "general",
    required: false,
    getValue: (d) => (Array.isArray(d?.categories) && d.categories.length > 0 ? "set" : ""),
    prompt: "Which category should this sit in?",
  },
  {
    key: "coverImage",
    label: "Cover image",
    section: "media",
    required: false,
    getValue: (d) => (d?.coverImage?.imageId ? "set" : ""),
    prompt: "Upload a cover photo in the Media section.",
  },
  {
    key: "price",
    label: "Price",
    section: "pricing",
    required: true,
    getValue: (d) => {
      const p = d?.priceInDefaultCurrency;
      return p != null && p !== "" && !Number.isNaN(Number(p)) ? String(p) : "";
    },
    prompt: "What price should we list it at (excl. tax)?",
  },
  {
    key: "taxClass",
    label: "Tax class",
    section: "pricing",
    required: true,
    getValue: (d) => d?.taxClassId?.trim?.() || d?.taxClassId || "",
    prompt: "Which tax class applies?",
  },
  {
    key: "quantity",
    label: "Quantity",
    section: "inventory",
    required: true,
    // Filled only when the merchant explicitly chose unlimited inventory or
    // gave a quantity - a fresh draft must prompt KAI to ask about stock.
    getValue: (d) => {
      if (d?.isProductInventoryUnlimited === true) return "unlimited";
      const q = d?.quantityAvailable;
      return q != null && q !== "" && !Number.isNaN(Number(q)) ? String(q) : "";
    },
    prompt: "How many do you have in stock? (or is it unlimited?)",
  },
];

export function getProductCompletionStatus(productDraft = {}) {
  const filled = [];
  const missing = [];
  const missingRequired = [];

  for (const def of FIELD_DEFS) {
    const value = def.getValue(productDraft);
    const isFilled = Boolean(value);
    if (isFilled) {
      filled.push({ ...def, value });
    } else {
      missing.push(def);
      if (def.required) missingRequired.push(def);
    }
  }

  const total = FIELD_DEFS.length;
  const done = filled.length;
  const percent = Math.round((done / total) * 100);

  return {
    filled,
    missing,
    missingRequired,
    percent,
    isComplete: missingRequired.length === 0,
    total,
    done,
  };
}

export function buildGuidanceContextMessage({ productDraft, productId, appliedFromAi = [] }) {
  const status = getProductCompletionStatus(productDraft);
  const name = productDraft?.productInformation?.name?.trim() || "this product";
  const applied =
    appliedFromAi.length > 0
      ? `KAI just filled from the cover photo: ${appliedFromAi.join(", ")}. `
      : "";

  const have = status.filled.map((f) => f.label).join(", ") || "nothing yet";
  const need = status.missingRequired.map((f) => f.label).join(", ") || "nothing critical";
  const nice = status.missing
    .filter((f) => !f.required)
    .map((f) => f.label)
    .join(", ");

  const draftNote = productId
    ? `The merchant is editing product id ${productId} on the Edit Product screen.`
    : `The merchant is on the Add Product screen with an UNSAVED draft. Do NOT call create_product until they ask to publish/save. Instead, help them fill the form conversationally. When they give you a value (brand, price, quantity, type, etc.), propose the right write tool (update_product for name/summary/description/brand/productTypeName/productConditionName, set_price, set_stock) so they can confirm and we'll apply it to their draft. For these proposals use the literal productId "draft". This draft has no real id yet and the dashboard intercepts the action locally, so do NOT search for the product first. Brands match case-insensitively and are created automatically on confirm if they don't exist. Never block on a missing brand; just propose update_product with that brandName.`;

  return `${draftNote}

${applied}Product: "${name}".
Already filled: ${have}.
Still required: ${need}.${nice ? ` Nice to have: ${nice}.` : ""}

Be warm and concise. One question at a time. Celebrate progress. Always ask about stock quantity if it's still missing. Never skip it. If they seem stuck, suggest they can type or tap the mic to talk. Refer to yourself as KAI (never "the AI"). Start by acknowledging what KAI already filled in, then ask for the most important missing field.`;
}

export { FIELD_DEFS };
